import { changeLocaleToMatchProfile, translate } from "./utils/localisation";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Box,
  Button,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Routes,
  Route,
  NavLink,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";

import { PetProfileSearch } from "./components/pet-profiles/PetProfileSearch";
import { PetProfileView } from "./components/pet-profiles/PetProfileView";
import { PetProfileEdit } from "./components/pet-profiles/PetProfileEdit";
import { UserProfileList } from "./components/user-profiles/UserProfileList";
import { UserProfileView } from "./components/user-profiles/UserProfileView";
import ClinicSummary from "./components/get-clinic-summary/ClinicSummary";
import FeatureConfiguratorV1 from "./components/feature-configurator/FeatureConfiguratorV1";
import { ROUTE_PATHS } from "./constants";
import { PetProfile, UserProfile } from "./types";
import SignInDialog from "./components/dialogs/SignInDialog";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import PetsOutlinedIcon from "@mui/icons-material/PetsOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import {
  isAuthenticated as checkIsAuthenticated,
  logout as doLogout,
} from "./auth";
import Home from "./components/home/Home";
import AuditLogsPage from "./components/audit-log/AuditLogsPage";
import PracticeConfigToolPage from "./components/practice-config-tool/PracticeConfigToolPage";
import PracticeConfigToolEditPage from "./components/practice-config-tool/PracticeConfigToolEditPage";
import { useEntitlements } from "./services/entitlement/useEntitlement";

export const DRAWER_WIDTH_MD = 228;
export const DRAWER_WIDTH_LG = 250;

function RequirePermission({
  children,
  allowed,
}: {
  children: React.ReactNode;
  allowed: boolean;
}) {
  if (!allowed) return <Navigate to="/" replace />;
  return <>{children}</>;
}

type AppContextType = {
  petProfiles: PetProfile[];
  userProfiles: UserProfile[];
  setUserProfiles: (userProfiles: UserProfile[]) => any;
  numberOfResults: number;
  setNumberOfResults: (page: number) => any;
  page: number;
  setPage: (page: number) => any;
  logout: () => void;
};

export const AppContext = React.createContext<AppContextType>({
  petProfiles: [],
  userProfiles: [],
  setUserProfiles: () => {},
  numberOfResults: 0,
  setNumberOfResults: () => {},
  page: 1,
  setPage: () => {},
  logout: () => {},
});

function App() {
  const [userProfiles, setUserProfiles] = useState<UserProfile[]>([]);
  const [page, setPage] = useState<number>(1);
  const [numberOfResults, setNumberOfResults] = useState<number>(0);
  const [openLoginDialog, setOpenLoginDialog] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(
    checkIsAuthenticated(),
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const { loaded: entitlementsLoaded, can } = useEntitlements();

  const canAskAi = entitlementsLoaded && can("ai:ask");
  const canSeePracticeTool =
    entitlementsLoaded && can("create_practice_tool:get");

  const navItems = ["Home", ...Object.keys(ROUTE_PATHS)].filter(text => {
    if (!entitlementsLoaded) return false;

    if (text === "Feature Configurator") return canAskAi;
    if (text === "Practice Config Tool") return canSeePracticeTool;

    return true;
  });

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const location = useLocation();
  const navigate = useNavigate();

  const navLabel = (key: string) => {
    // Prefer dashed nested keys like 'audit-logs.title' (convert spaces to '-'),
    // fallback to underscore form for backward compatibility.
    const dashed = key.toLowerCase().replace(/\s+/g, "-");
    const dashedKey = `${dashed}.title`;
    const underscored = key.toLowerCase().replace(/\s+/g, "_");
    const result = translate(dashedKey);
    if (result && result !== dashedKey) return result;
    return translate(underscored);
  };

  useEffect(() => setIsAuthenticated(checkIsAuthenticated()), []);

  useEffect(() => {
    async function getStuff() {
      changeLocaleToMatchProfile();
    }
    if (isAuthenticated) void getStuff();
  }, [isAuthenticated]);

  const login = () => setOpenLoginDialog(true);
  const logout = useCallback(() => {
    doLogout();
    setIsAuthenticated(false);
    setOpenLoginDialog(false);
  }, []);
  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    setOpenLoginDialog(false);
  };

  const BROWN = "#210C03";

  const NAV_ICONS: Record<string, { left: React.ReactNode }> = {
    Home: { left: <HomeRoundedIcon fontSize="small" /> },
    "User Profiles": { left: <PeopleOutlineIcon fontSize="small" /> },
    "Pet Medical Records": { left: <PetsOutlinedIcon fontSize="small" /> },
    "Get Clinic Summary": { left: <SummarizeOutlinedIcon fontSize="small" /> },
    "Feature Configurator": { left: <TuneOutlinedIcon fontSize="small" /> },
    "Practice Config Tool": { left: <BusinessOutlinedIcon fontSize="small" /> },
    "Audit Logs": { left: <ArticleOutlinedIcon fontSize="small" /> },
  };

  const drawerList = (
    <List dense sx={{ px: 1 }}>
      {isAuthenticated &&
        navItems.map(text => {
          const to =
            text === "Home"
              ? "/"
              : ROUTE_PATHS[text as keyof typeof ROUTE_PATHS];
          const isActive = to === location.pathname;
          return (
            <ListItem key={text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={NavLink}
                to={to}
                onClick={() => setMobileOpen(false)}
                selected={isActive}
                sx={{
                  mx: 1,
                  px: 1.25,
                  py: 1.1,
                  borderRadius: 2.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  transition:
                    "background-color 160ms ease, color 160ms ease, transform 120ms ease",
                  "& .MuiListItemIcon-root": {
                    minWidth: 30,
                    mr: 0,
                    color: isActive ? BROWN : alpha(BROWN, 0.55),
                  },
                  "& .MuiListItemText-root": {
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                  },
                  "& .MuiListItemText-primary": {
                    fontWeight: isActive ? 700 : 600,
                    color: isActive ? BROWN : alpha(BROWN, 0.85),
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    letterSpacing: 0.15,
                    fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.9rem" },
                  },
                  "&.Mui-selected": {
                    bgcolor: alpha(BROWN, 0.08),
                    boxShadow: `inset 0 0 0 1px ${alpha(BROWN, 0.15)}`,
                    "&:hover": { bgcolor: alpha(BROWN, 0.12) },
                  },
                  "&:hover": {
                    bgcolor: alpha(BROWN, 0.06),
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <ListItemIcon>
                  {NAV_ICONS[text]?.left ?? (
                    <ChevronRightIcon fontSize="small" />
                  )}
                </ListItemIcon>

                <ListItemText primary={navLabel(text)} />

                <Box
                  sx={{
                    ml: 0.5,
                    color: isActive ? BROWN : alpha(BROWN, 0.45),
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </Box>
              </ListItemButton>
            </ListItem>
          );
        })}
    </List>
  );

  const Sidebar = (
    <Box
      component="nav"
      sx={{
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        flex: "0 0 auto",
        width: { md: DRAWER_WIDTH_MD, lg: DRAWER_WIDTH_LG },
        height: "100vh",
        position: "sticky",
        top: 0,
        borderRight: "none",
        backgroundImage: `linear-gradient(180deg,
          ${alpha(theme.palette.background.paper, 1)} 0%,
          ${alpha(theme.palette.background.paper, 0.96)} 100%)`,
      }}
    >
      <Toolbar />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ overflow: "auto", py: 1, flexGrow: 1 }}>{drawerList}</Box>
        <Box
          sx={{
            borderTop: `1px solid ${alpha(BROWN, 0.1)}`,
            p: 1.5,
            mt: 1,
          }}
        >
          <ListItemButton
            onClick={logout}
            sx={{
              borderRadius: 2,
              transition:
                "background-color 160ms ease, color 160ms ease, transform 120ms ease",
              "&:hover": {
                bgcolor: alpha(BROWN, 0.06),
                transform: "translateY(-1px)",
              },
            }}
          >
            <ListItemIcon sx={{ color: alpha(BROWN, 0.65), minWidth: 32 }}>
              <LogoutRoundedIcon />
            </ListItemIcon>
            <ListItemText
              primary={translate("header.logout-button-text")}
              sx={{
                "& .MuiListItemText-primary": {
                  fontWeight: 600,
                  fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                  color: alpha(BROWN, 0.9),
                },
              }}
            />
          </ListItemButton>
        </Box>
      </Box>
    </Box>
  );

  const contextValue = useMemo(
    () => ({
      petProfiles: [] as PetProfile[],
      userProfiles,
      setUserProfiles,
      numberOfResults,
      setNumberOfResults,
      page,
      setPage,
      logout,
    }),
    [
      userProfiles,
      setUserProfiles,
      numberOfResults,
      setNumberOfResults,
      page,
      setPage,
      logout,
    ],
  );

  return (
    <AppContext.Provider value={contextValue}>
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        <SignInDialog
          open={openLoginDialog}
          onClose={() => setOpenLoginDialog(false)}
          onLoginSuccess={handleLoginSuccess}
        />
        <CssBaseline />

        <AppBar
          position="fixed"
          sx={{
            zIndex: t => t.zIndex.drawer + 1,
            backgroundColor: "#210C03",
            color: "#fff",
            boxShadow: "0 2px 6px rgba(33, 12, 3, 0.3)",
          }}
        >
          <Toolbar>
            {isAuthenticated && !isDesktop && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="open navigation"
                onClick={() => setMobileOpen(true)}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Box
              onClick={() => navigate("/")}
              sx={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                flexGrow: 1,
              }}
              aria-label="Go to Home"
              title={translate("header.top-text")}
            >
              <Typography variant="h6" noWrap>
                {translate("header.top-text")}
              </Typography>
            </Box>

            {!isAuthenticated && (
              <Button onClick={login} variant="contained" color="success">
                {translate("home-screen.login-text")}
              </Button>
            )}
          </Toolbar>
        </AppBar>

        {isAuthenticated && Sidebar}

        {isAuthenticated && (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: "block", md: "none" },
              [`& .MuiDrawer-paper`]: {
                width: Math.min(DRAWER_WIDTH_MD, 280),
                boxSizing: "border-box",
                borderRight: "none",
                backgroundImage: `linear-gradient(180deg,
                  ${alpha(theme.palette.background.paper, 1)} 0%,
                  ${alpha(theme.palette.background.paper, 0.96)} 100%)`,
              },
            }}
          >
            <Toolbar />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ overflow: "auto", py: 1, flexGrow: 1 }}>
                {drawerList}
              </Box>

              <Box
                sx={{
                  borderTop: `1px solid ${alpha(BROWN, 0.1)}`,
                  p: 1.5,
                  mt: 1,
                }}
              >
                <ListItemButton
                  onClick={logout}
                  sx={{
                    borderRadius: 2,
                    transition:
                      "background-color 160ms ease, color 160ms ease, transform 120ms ease",
                    "&:hover": {
                      bgcolor: alpha(BROWN, 0.06),
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{ color: alpha(BROWN, 0.65), minWidth: 32 }}
                  >
                    <LogoutRoundedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={translate("header.logout-button-text")}
                    sx={{
                      "& .MuiListItemText-primary": {
                        fontWeight: 600,
                        fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        color: alpha(BROWN, 0.9),
                      },
                    }}
                  />
                </ListItemButton>
              </Box>
            </Box>
          </Drawer>
        )}

        <Box
          component="main"
          sx={{
            flex: 1,
            px: 2,
            py: 2,
          }}
        >
          <Toolbar />
          <Routes>
            <Route
              path={ROUTE_PATHS["User Profiles"]}
              element={
                <RequireAuth isAuthenticated={isAuthenticated}>
                  <UserProfileList />
                </RequireAuth>
              }
            />
            <Route
              path={ROUTE_PATHS["Pet Medical Records"]}
              element={
                <RequireAuth isAuthenticated={isAuthenticated}>
                  <PetProfileSearch />
                </RequireAuth>
              }
            />
            <Route
              path={ROUTE_PATHS["Get Clinic Summary"]}
              element={
                <RequireAuth isAuthenticated={isAuthenticated}>
                  <ClinicSummary />
                </RequireAuth>
              }
            />
            <Route
              path={ROUTE_PATHS["Feature Configurator"]}
              element={
                <RequireAuth isAuthenticated={isAuthenticated}>
                  <RequirePermission allowed={canAskAi}>
                    <FeatureConfiguratorV1 />
                  </RequirePermission>
                </RequireAuth>
              }
            />

            <Route
              path={ROUTE_PATHS["Audit Logs"]}
              element={
                <RequireAuth isAuthenticated={isAuthenticated}>
                  <AuditLogsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/pet-profile-edit/:petId"
              element={
                <RequireAuth isAuthenticated={isAuthenticated}>
                  <PetProfileEdit />
                </RequireAuth>
              }
            />
            <Route
              path="/pet-profile/:petId"
              element={
                <RequireAuth isAuthenticated={isAuthenticated}>
                  <PetProfileView />
                </RequireAuth>
              }
            />
            <Route
              path={ROUTE_PATHS["Practice Config Tool"]}
              element={
                <RequireAuth isAuthenticated={isAuthenticated}>
                  <RequirePermission allowed={canSeePracticeTool}>
                    <PracticeConfigToolPage />
                  </RequirePermission>
                </RequireAuth>
              }
            />

            <Route
              path="/user-profile/:userId"
              element={
                <RequireAuth isAuthenticated={isAuthenticated}>
                  <UserProfileView />
                </RequireAuth>
              }
            />

            <Route
              path="/practice-config-tool/edit/:practiceKinshipId"
              element={
                <RequireAuth isAuthenticated={isAuthenticated}>
                  <RequirePermission allowed={canSeePracticeTool}>
                    <PracticeConfigToolEditPage />
                  </RequirePermission>
                </RequireAuth>
              }
            />

            <Route
              path="/*"
              element={
                <RequireAuth isAuthenticated={isAuthenticated}>
                  <Home />
                </RequireAuth>
              }
            />
            <Route
              path="/login"
              element={
                <LoggedOutOnly isAuthenticated={isAuthenticated}>
                  <div>{translate("home-screen.please-login-text")}</div>
                </LoggedOutOnly>
              }
            />
          </Routes>
        </Box>
      </Box>
    </AppContext.Provider>
  );
}

type AuthGateProps = Readonly<{
  children: React.ReactNode;
  isAuthenticated: boolean;
}>;

function LoggedOutOnly({ children, isAuthenticated }: AuthGateProps) {
  const location = useLocation();
  if (isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

function RequireAuth({ children, isAuthenticated }: AuthGateProps) {
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

export default App;
